import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { safeAbsurd } from '@ipfabric/utils';
import { Dialog } from 'components/Dialog/Dialog';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import type { AuthResponseData } from 'components/GlobalState/commands/saveAuth';
import { refreshTokenExpirationLocalStorage } from 'components/GlobalState/refreshTokenExpirationLocalStorage';
import { constVoid } from 'fp-ts/lib/function';
import { useEffect, useRef, useState } from 'react';
import { type FC, memo } from 'react';
import { useNavigatePushPath } from 'utils/history/useNavigatePushPath';
import { useApi } from 'utils/request/useApi';

const SHOW_TIME_BEFORE_EXPIRATION = 1000 * 60;

const BareSessionExtensionDialog: FC = () => {
  const api = useApi();
  const ctxProps = useGlobalStateContextSelectors({
    authenticated: (s) => s.loginStatus === 'AUTHENTICATED',
  });
  const navigatePushPath = useNavigatePushPath();
  const expirationIndicatorRef = useRef<HTMLDivElement>(null);

  const refreshTokenExpiresAt = refreshTokenExpirationLocalStorage.useValue();

  const [tokenState, setTokenState] = useState<'ok' | 'expiring' | 'expired'>(
    'ok',
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies(ctxProps.authenticated): re-trigger on authentication change
  useEffect(() => {
    setTokenState('ok');

    if (refreshTokenExpiresAt === null) {
      // show expired dialog in case of logout from another tab;
      // if logged out from this tab, dialog is not rendered as ctxProps.authenticated === false.
      setTokenState('expired');
      return undefined;
    }

    const timeToWarn = Math.max(
      refreshTokenExpiresAt - Date.now() - SHOW_TIME_BEFORE_EXPIRATION,
      0,
    );

    let animationFrameId: number | null;
    const updateExpirationIndicator = () => {
      const percentageToExpire =
        (refreshTokenExpiresAt! - Date.now()) / SHOW_TIME_BEFORE_EXPIRATION;
      if (percentageToExpire >= 0) {
        if (expirationIndicatorRef.current) {
          expirationIndicatorRef.current.style.width = `${percentageToExpire * 100}%`;
        }
        animationFrameId = window.requestAnimationFrame(
          updateExpirationIndicator,
        );
      }
    };

    let timeout = window.setTimeout(() => {
      setTokenState('expiring');
      timeout = window.setTimeout(() => {
        setTokenState('expired');
      }, refreshTokenExpiresAt - Date.now());
      updateExpirationIndicator();
    }, timeToWarn);

    return () => {
      window.clearTimeout(timeout);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [ctxProps.authenticated, refreshTokenExpiresAt]);

  const [prolonging, setProlonging] = useLoadingState();

  const prolongSession = useStableCallback(async () =>
    withLoadingIndicator(setProlonging)(() =>
      api.request<AuthResponseData>({
        url: '/auth/token',
        method: 'POST',
      }),
    ),
  );

  const logOut = useStableCallback(() => {
    navigatePushPath('/logout');
  });

  if (!ctxProps.authenticated) {
    return null;
  }

  switch (tokenState) {
    case 'ok':
      return null;
    case 'expiring':
      return (
        <Dialog
          headerMsg='Session will expire soon'
          primaryBtnMsg='Continue'
          primaryBtnLoading={prolonging === 'LOADING'}
          onPrimaryClick={prolongSession}
          closable={false}
          onClose={constVoid}
        >
          <div
            className='bg-brand absolute left-0 top-0 h-1 w-full'
            ref={expirationIndicatorRef}
          />
          Your session is going to expire. Please confirm that you want to
          continue working, otherwise you will be logged out in 1 minute.
        </Dialog>
      );
    case 'expired':
      return (
        <Dialog
          headerMsg='Session expired'
          primaryBtnMsg='Continue'
          // Intentionally kept the button always disabled in this state.
          // If we'd make the "log out" button primary, user may accidentally
          // click it instead of "Continue" when session is nearly expired.
          primaryDisabled
          onPrimaryClick={constVoid}
          secondaryBtnMsg='Log out'
          onSecondaryClick={logOut}
          closable={false}
          onClose={constVoid}
        >
          Your session has expired. To preserve any unsaved work, please{' '}
          <strong>log in from a different browser tab</strong>, then return
          there.
        </Dialog>
      );
    default:
      safeAbsurd(tokenState);
      return null;
  }
};

export const SessionExtensionDialog = memo(BareSessionExtensionDialog);
